/**
 * Vorlage „Anschwitzen“.
 *
 * Zwiebelwürfel in wenig Fett bei mittlerer Hitze. Ein Kochlöffel schiebt sie
 * hin und her; sie werden glasig, aber nicht braun.
 */
import { ease, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { compact, faded } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { sizzle, steam } from '../parts/effects.ts';
import { cube, scatter } from '../parts/food.ts';
import { counter, doubleArrow } from '../parts/stage.ts';
import { GRATE_Y, HEAT_LEVELS, HEAT_POWER, heatFromText, stove, stoveKnob } from '../parts/stove.ts';
import type { HeatLevel } from '../parts/stove.ts';
import { place, woodenSpoon } from '../parts/utensils.ts';
import { pan } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const PAN = { cx: 96, width: 124 } as const;
const RIM_Y = GRATE_Y - 13 - PAN.width * 0.1;
const FLOOR_Y = RIM_Y + 5;
const KNOB_X = 214;

/** Zeitplan. */
const TIME = { glassy: [0.08, 0.7], reset: [0.9, 1] } as const;

/** Art und Größe der Stücke. */
const ITEMS = {
  onion: { count: 20, size: 7.5 },
  garlic: { count: 26, size: 5 },
} as const;

/** Vorlage „Anschwitzen“. */
export const stoveSweatTemplate = defineTemplate({
  id: 'stove.sweat',
  group: 'stove',
  title: 'Anschwitzen',
  description:
    'Zwiebelwürfel werden in wenig Fett bei mittlerer Hitze unter Rühren gegart, bis sie glasig sind. Sie sollen dabei nicht braun werden.',
  durationSeconds: 5,
  keywords: ['anschwitzen', 'andünsten', 'glasig', 'dünsten'],
  stillTime: 0.72,
  parameters: [
    choice('item', 'Zutat', { onion: 'Zwiebel', garlic: 'Knoblauch' }, 'onion'),
    choice('heat', 'Hitze', HEAT_LEVELS, 'medium'),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    return {
      item: lower.includes('knoblauch') && !lower.includes('zwiebel') ? 'garlic' : undefined,
      ...heatFromText(text),
    };
  },
  scene(values, t) {
    const item = ITEMS[textValue(values, 'item', 'onion') as keyof typeof ITEMS];
    const power = HEAT_POWER[textValue(values, 'heat', 'medium') as HeatLevel];
    const vessel = pan(PAN.cx, RIM_Y, PAN.width);
    const burner = stove(PAN.cx, power, t);
    const stir = wave(t, 2);
    const glassy =
      ease.inOutSine(segment(t, ...TIME.glassy)) * (1 - ease.inOutSine(segment(t, ...TIME.reset)));
    const spoonX = PAN.cx + stir * 26;

    // Der Löffel schiebt die Würfel mit, je näher, desto mehr.
    const positions = scatter(PAN.cx, FLOOR_Y, PAN.width * 0.32, 11, item.count, 21).map(
      (position, index) => {
        const push = Math.max(0, 1 - Math.abs(position.x - spoonX) / 30);
        return {
          x: position.x + wave(t, 2, -0.08 + index * 0.003) * 5 * push,
          y: position.y,
          turn: position.turn + stir * 10 * push,
        };
      },
    );
    const raw: SceneNode[] = positions.map((position) => cube(position, item.size, 'snow'));
    const cooked: SceneNode[] = positions.map((position) => cube(position, item.size, 'tint', STROKE.fine));
    return compact([
      ...counter(),
      ...burner.back,
      ...vessel.back,
      ...raw,
      faded(glassy, cooked),
      place(spoonX, FLOOR_Y + 2, 24 + stir * 8, woodenSpoon()),
      ...vessel.front,
      ...burner.front,
      ...sizzle(PAN.cx, FLOOR_Y - 4, 40, 0.3, t, 6),
      ...steam(PAN.cx - 10, FLOOR_Y - 18, t, { count: 2, spread: 30, height: 24, strength: 0.5 }),
      stoveKnob(KNOB_X, power),
    ]);
  },
  hints() {
    return [doubleArrow({ x: PAN.cx - 40, y: FLOOR_Y - 26 }, { x: PAN.cx + 40, y: FLOOR_Y - 26 })];
  },
});
